import { Node, NodeProps } from '@xyflow/react';
import { useSetAtom } from 'jotai';
import { FC, memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { EmailIcon, GroupsIcon } from '@/lib/v2/icons/solid';
import { CommonFlowCard } from '@/lib/v3/components';

import {
  atomNodeIdSelected,
  atomSidePanelIsOpen,
} from '@/src/modules/AutomationModuleNew/atoms/flows';
import { TRANSLATIONS } from '@/src/modules/AutomationModuleNew/constants/flows';
import { SendEmailCard } from '@/src/modules/AutomationModuleNew/screens/JourneysScreen/components';
import GroupManageContactCard from '@/src/modules/AutomationModuleNew/screens/JourneysScreen/components/GroupManageContactCard';

import { ActionNodeData } from '@/modules/AutomationModuleNew/types/flowsTypes';

export interface ActionNodeProps extends NodeProps<Node<ActionNodeData>> {
  onDelete?: (nodeId: string) => void;
}

const ActionNode: FC<ActionNodeProps> = (props) => {
  const { t } = useTranslation();
  const { data, onDelete } = props;
  const setSidePanelIsOpen = useSetAtom(atomSidePanelIsOpen);
  const setNodeIdSelected = useSetAtom(atomNodeIdSelected);

  const handleOpenSidePanel = useCallback(
    (nodeId: string | number) => {
      setNodeIdSelected(nodeId);
      setSidePanelIsOpen(true);
    },
    [setNodeIdSelected, setSidePanelIsOpen]
  );

  const RenderSubTypeComponent = () => {
    if (data.type === 'groupsManageContact') {
      const { id } = data;

      return (
        <GroupManageContactCard
          borderColor="action"
          data={data}
          editAction={{
            label: t('ActionNode.edit'),
            onClick: handleOpenSidePanel,
          }}
          icon={<GroupsIcon />}
          id={id}
          title={t(TRANSLATIONS.nodeTypes.groupsManageContact)}
          onDelete={onDelete}
        />
      );
    }
    if (data.type === 'sendEmail') {
      const { id } = data;

      return (
        <SendEmailCard
          borderColor={'action'}
          data={data}
          editAction={{
            label: t('ActionNode.edit'),
          }}
          icon={<EmailIcon />}
          id={id}
          title={t(TRANSLATIONS.nodeTypes.sendEmail)}
          onDelete={onDelete}
        />
      );
    }
    if (data.type === 'sendSMS') {
      const { id, input } = data;
      const { actionId } = input;
      const info = actionId ? actionId.toString() : undefined;
      return (
        <CommonFlowCard
          borderColor={'action'}
          editAction={{
            label: t('ActionNode.edit'),
            onClick: () => alert('Side panel content under construction 🛠️'),
          }}
          icon={<EmailIcon />}
          id={id}
          info={info}
          title={t(TRANSLATIONS.nodeTypes.sendSMS)}
          onDelete={onDelete}
        />
      );
    }
    if (data.type === 'tagsManageContact') {
      const { id, input } = data;
      const info = input.addTagIds.map((tag) => tag.value).join(', ');

      return (
        <CommonFlowCard
          borderColor={'action'}
          editAction={{
            label: t('ActionNode.edit'),
            onClick: () => alert('Side panel content under construction 🛠️'),
          }}
          icon={<GroupsIcon />}
          id={id}
          info={info}
          title={t(TRANSLATIONS.nodeTypes.tagsManageContact)}
          onDelete={onDelete}
        />
      );
    }
    if (data.type === 'httpRequest') {
      const { id, input } = data;
      const info = input.url ? input.url : undefined;
      return (
        <CommonFlowCard
          borderColor={'action'}
          editAction={{
            label: t('ActionNode.edit'),
            onClick: () => alert('Side panel content under construction 🛠️'),
          }}
          icon={<EmailIcon />}
          id={id}
          info={info}
          title={t(TRANSLATIONS.nodeTypes.httpRequest)}
          onDelete={onDelete}
        />
      );
    }
    return null;
  };

  return <RenderSubTypeComponent />;
};

export default memo(ActionNode);
